/**
 * Prompt-pattern endpoints: the administrator's catalog and the wizard's user-facing list (TASK-0039).
 *
 * Two surfaces in one module, because they describe one resource: the administrative CRUD an administrator edits the
 * catalog with, and the two reads/writes the wizard performs — the level-scoped list it builds the pattern step from, and
 * the draft write that stores a selection. The catalog is a fourth axis of the product, beside the framework file, the
 * prompt level and the target model, so its list is deliberately separate from the steps payload: it changes when an
 * administrator edits it, not when a framework file changes.
 */

import { apiClient } from '@/lib/apiClient';
import type {
  CreatePromptPatternPayload,
  PromptPattern,
  PromptLevel,
  SelectablePromptPattern,
  UpdatePromptPatternPayload,
  WizardDraft,
} from '@/types/api';

/** Administrative prompt-pattern operations; every call is role-gated on the API side. */
export const adminPromptPatternApi = {
  /** Lists the catalog, including soft-deleted patterns when asked. */
  async list(includeDeleted = false): Promise<PromptPattern[]> {
    const { data } = await apiClient.get<PromptPattern[]>('/admin/prompt-patterns', {
      params: { includeDeleted },
    });
    return data;
  },

  /** Creates a pattern and returns it as the server stored it. */
  async create(payload: CreatePromptPatternPayload): Promise<PromptPattern> {
    const { data } = await apiClient.post<PromptPattern>('/admin/prompt-patterns', payload);
    return data;
  },

  /** Edits a pattern and returns it as the server stored it. */
  async update(patternId: string, payload: UpdatePromptPatternPayload): Promise<PromptPattern> {
    const { data } = await apiClient.put<PromptPattern>(
      `/admin/prompt-patterns/${patternId}`,
      payload,
    );
    return data;
  },

  /** Enables or disables a pattern. */
  async setState(patternId: string, isActive: boolean): Promise<PromptPattern> {
    const { data } = await apiClient.post<PromptPattern>(
      `/admin/prompt-patterns/${patternId}/state`,
      {
        isActive,
      },
    );
    return data;
  },

  /** Soft-deletes a pattern; the row survives for the drafts and prompts that named it. */
  async remove(patternId: string): Promise<PromptPattern> {
    const { data } = await apiClient.delete<PromptPattern>(`/admin/prompt-patterns/${patternId}`);
    return data;
  },

  /** Brings a soft-deleted pattern back, disabled. */
  async restore(patternId: string): Promise<PromptPattern> {
    const { data } = await apiClient.post<PromptPattern>(
      `/admin/prompt-patterns/${patternId}/restore`,
    );
    return data;
  },
};

/** The wizard's prompt-pattern operations. */
export const promptPatternApi = {
  /** Lists the patterns a user may select for a level, each flagged with whether the policy suggests it. */
  async list(level: PromptLevel): Promise<SelectablePromptPattern[]> {
    const { data } = await apiClient.get<SelectablePromptPattern[]>('/wizard/patterns', {
      params: { level },
    });
    return data;
  },

  /**
   * Stores the whole pattern selection of a framework draft.
   *
   * A replacement rather than a delta, so a deselection is expressible. The write is conditional on the draft's revision,
   * exactly like every other framework auto-save, so a stale tab cannot overwrite a newer selection.
   */
  async saveSelection(
    sessionId: string,
    patternIds: readonly string[],
    revision?: number | null,
  ): Promise<WizardDraft> {
    const { data } = await apiClient.put<WizardDraft>(
      `/wizard/frameworks/sessions/${sessionId}/patterns`,
      { patternIds: [...patternIds] },
      revision == null ? undefined : { headers: { 'If-Match': String(revision) } },
    );
    return data;
  },
};
